import * as THREE from 'three';

import { buildBoneRefData } from '../../lib/skeleton/boneRefs';
import type { Skeleton } from '../../lib/skeleton/core';

type ScenePoseTargets = {
  bonesByName: Map<string, THREE.Bone[]>;
  skinnedBonesByName: Map<string, THREE.Bone[]>;
  bonesByFullPathRef: Map<string, THREE.Bone[]>;
  skinnedBonesByFullPathRef: Map<string, THREE.Bone[]>;
  skinnedMeshes: THREE.SkinnedMesh[];
  restLocalByBone: Map<THREE.Bone, THREE.Matrix4>;
};

const poseTargetCacheByScene = new WeakMap<THREE.Object3D, ScenePoseTargets>();

function matrixMaxAbsDelta(a: THREE.Matrix4, b: THREE.Matrix4) {
  let maxDelta = 0;
  for (let index = 0; index < 16; index++) {
    maxDelta = Math.max(
      maxDelta,
      Math.abs((a.elements[index] ?? 0) - (b.elements[index] ?? 0)),
    );
  }
  return maxDelta;
}

function selectBestRestLocalMatches(
  sceneBones: THREE.Bone[],
  restLocalByBone: Map<THREE.Bone, THREE.Matrix4>,
  skeletonRestLocal: THREE.Matrix4,
): THREE.Bone[] {
  if (sceneBones.length <= 1) return sceneBones;

  // FBXLoader can expose same-name wrapper bones alongside the real skinned
  // bone. Pose only the targets whose rest local matches Cartographer's bone.
  const scored = sceneBones
    .map((sceneBone) => {
      const restLocal = restLocalByBone.get(sceneBone);
      return restLocal
        ? {
            sceneBone,
            score: matrixMaxAbsDelta(restLocal, skeletonRestLocal),
          }
        : null;
    })
    .filter((entry): entry is { sceneBone: THREE.Bone; score: number } =>
      Boolean(entry),
    );
  if (scored.length === 0) return sceneBones;

  const bestScore = Math.min(...scored.map((entry) => entry.score));
  const bestMatches = scored
    .filter((entry) => entry.score <= bestScore + 1e-3)
    .map((entry) => entry.sceneBone);

  return bestMatches.length > 0 ? bestMatches : sceneBones;
}

function registerTargetBone(
  map: Map<string, THREE.Bone[]>,
  key: string,
  bone: THREE.Bone,
): void {
  const current = map.get(key);
  if (current) {
    if (!current.includes(bone)) current.push(bone);
  } else {
    map.set(key, [bone]);
  }
}

function ensureRestLocal(
  restLocalByBone: Map<THREE.Bone, THREE.Matrix4>,
  bone: THREE.Bone,
): void {
  if (!restLocalByBone.has(bone)) {
    // Capture clone-local rest transform once so pose application can remain
    // delta-based and avoid forcing extracted absolute FBX transforms.
    const restLocal = new THREE.Matrix4().compose(
      bone.position.clone(),
      bone.quaternion.clone(),
      bone.scale.clone(),
    );
    restLocalByBone.set(bone, restLocal);
  }
}

function registerBoneWithMaps(
  nameMap: Map<string, THREE.Bone[]>,
  fullPathMap: Map<string, THREE.Bone[]>,
  restLocalByBone: Map<THREE.Bone, THREE.Matrix4>,
  bone: THREE.Bone,
  fullPathRef: string | null,
): void {
  registerTargetBone(nameMap, bone.name, bone);
  if (fullPathRef) {
    registerTargetBone(fullPathMap, fullPathRef, bone);
  }
  ensureRestLocal(restLocalByBone, bone);
}

function getSceneBoneParentIndex(
  bone: THREE.Bone,
  indexByBone: Map<THREE.Bone, number>,
): number | null {
  let current: THREE.Object3D | null = bone.parent;
  while (current) {
    if (current instanceof THREE.Bone) {
      const parentIndex = indexByBone.get(current);
      if (parentIndex != null) return parentIndex;
    }
    current = current.parent;
  }
  return null;
}

function getScenePoseTargets(scene: THREE.Object3D): ScenePoseTargets {
  const cached = poseTargetCacheByScene.get(scene);
  if (cached) return cached;

  const bonesByName = new Map<string, THREE.Bone[]>();
  const skinnedBonesByName = new Map<string, THREE.Bone[]>();
  const bonesByFullPathRef = new Map<string, THREE.Bone[]>();
  const skinnedBonesByFullPathRef = new Map<string, THREE.Bone[]>();
  const skinnedMeshes: THREE.SkinnedMesh[] = [];
  const restLocalByBone = new Map<THREE.Bone, THREE.Matrix4>();
  const sceneBones: THREE.Bone[] = [];
  const skinnedMeshBones: THREE.Bone[] = [];

  scene.traverse((object) => {
    if (object instanceof THREE.Bone) {
      sceneBones.push(object);
      ensureRestLocal(restLocalByBone, object);
    }

    if (!(object instanceof THREE.SkinnedMesh) || !object.skeleton) return;
    skinnedMeshes.push(object);
    for (const bone of object.skeleton.bones) {
      if (bone) {
        skinnedMeshBones.push(bone);
        ensureRestLocal(restLocalByBone, bone);
      }
    }
  });

  const indexBySceneBone = new Map<THREE.Bone, number>();
  for (let i = 0; i < sceneBones.length; i++) {
    indexBySceneBone.set(sceneBones[i], i);
  }
  const refData = buildBoneRefData(
    sceneBones.map((bone, index) => ({
      index,
      parentIndex: getSceneBoneParentIndex(bone, indexBySceneBone),
      name: bone.name,
    })),
  );

  for (let i = 0; i < sceneBones.length; i++) {
    const bone = sceneBones[i];
    const fullPathRef = refData.fullPathRefByIndex[i] ?? null;
    registerBoneWithMaps(
      bonesByName,
      bonesByFullPathRef,
      restLocalByBone,
      bone,
      fullPathRef,
    );
  }

  for (const bone of skinnedMeshBones) {
    const sceneBoneIndex = indexBySceneBone.get(bone);
    const fullPathRef =
      sceneBoneIndex != null
        ? (refData.fullPathRefByIndex[sceneBoneIndex] ?? null)
        : null;
    registerBoneWithMaps(
      skinnedBonesByName,
      skinnedBonesByFullPathRef,
      restLocalByBone,
      bone,
      fullPathRef,
    );
  }

  const targets = {
    bonesByName,
    skinnedBonesByName,
    bonesByFullPathRef,
    skinnedBonesByFullPathRef,
    skinnedMeshes,
    restLocalByBone,
  };
  poseTargetCacheByScene.set(scene, targets);
  return targets;
}

export function applySkeletonPoseToSceneBones(
  scene: THREE.Object3D,
  skeleton: Skeleton,
): void {
  const {
    bonesByName,
    skinnedBonesByName,
    bonesByFullPathRef,
    skinnedBonesByFullPathRef,
    skinnedMeshes,
    restLocalByBone,
  } = getScenePoseTargets(scene);
  const localMatrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();

  for (const bone of skeleton.bones) {
    const usePathIdentity = skeleton.isAmbiguousName(bone.name);
    const targetRef = usePathIdentity ? bone.internalFullPathRef : bone.name;
    const skinnedBones = usePathIdentity
      ? skinnedBonesByFullPathRef.get(targetRef)
      : skinnedBonesByName.get(targetRef);
    const sceneBones =
      skinnedBones && skinnedBones.length > 0
        ? skinnedBones
        : usePathIdentity
          ? bonesByFullPathRef.get(targetRef)
          : bonesByName.get(targetRef);
    if (!sceneBones || sceneBones.length === 0) continue;
    const resolvedSceneBones = selectBestRestLocalMatches(
      sceneBones,
      restLocalByBone,
      bone.restLocal,
    );

    for (const sceneBone of resolvedSceneBones) {
      const sceneRestLocal = restLocalByBone.get(sceneBone);
      if (!sceneRestLocal) continue;

      localMatrix.multiplyMatrices(sceneRestLocal, bone.poseLocal);
      localMatrix.decompose(position, quaternion, scale);

      sceneBone.matrixAutoUpdate = true;
      sceneBone.position.copy(position);
      sceneBone.quaternion.copy(quaternion);
      sceneBone.scale.copy(scale);
      sceneBone.updateMatrix();
    }
  }

  scene.updateMatrixWorld(true);
  const updatedSkeletons = new Set<THREE.Skeleton>();
  for (const skinnedMesh of skinnedMeshes) {
    if (updatedSkeletons.has(skinnedMesh.skeleton)) continue;
    skinnedMesh.skeleton.update();
    updatedSkeletons.add(skinnedMesh.skeleton);
  }
}
